import React from 'react';
import { Box } from '@saviynt/design-system';

import useFormTranslations from '../../../../hooks/use-form-translations';
import type { CheckboxOption } from '../checkbox/checkbox';
import { type FormDropdownProps } from '../Dropdown/SingleSelect';
import FormDropdown from '../Dropdown/SingleSelect';
import FormInputText from '../TextInput/TextInput';
import { type ToggleGroupField } from '../ToggleGroup/toggleGroup';
import FormToggleGroup from '../ToggleGroup/toggleGroup';

import './FormBuilder.css';

export type InputFieldKind = 'singleline' | 'multiline' | 'search';

// Use the same interface as CheckboxGroup for consistency
export interface InputFields {
  type: FieldTypes | null;
  label?: string | null;
  pattern?: string | null;
  password?: boolean;
  required?: boolean;
  guideMe?: boolean;
  errorMessage?: string;
  inputDescription?: string | null;
  payloadAtrributeName?: string;
  attributeName?: string;
  options?: CheckboxOption[]; // Changed from CheckboxOptions[] to CheckboxOption[]
  inputValueStatus?: (status: {
    attributeName: string;
    value: string;
    hasError: boolean;
    payloadAtrributeName: string;
  }) => void;
  fieldDisabled?: boolean;
  defaultValue?: string;
  kind?: InputFieldKind;
  isTooltip?: boolean;
  tooltipText?: string;
  tooltipIconColor?: string;
  placeholder?: string | null;
  attributeKey?: string;
}

interface FormBuilderProps {
  fields: (InputFields | ToggleGroupField | FormDropdownProps)[];
  className?: string;
}

export enum FieldTypes {
  TextInputSingleLine = 'TextField',
  Dropdown = 'Dropdown',
  CheckBoxes = 'checkbox',
  ToggleGroup = 'ToggleGroup',
}

FormBuilder.defaultProps = {
  className: '',
};

/**
 * Form Builder component
 */
function FormBuilder({ fields, className = '' }: FormBuilderProps) {
  const { translateFormField, translatePlaceholder } = useFormTranslations();

  const isInputField = (
    field: InputFields | ToggleGroupField | FormDropdownProps
  ): field is InputFields =>
    field.type === FieldTypes.TextInputSingleLine ||
    field.type === FieldTypes.CheckBoxes;

  const isToggleGroupField = (
    field: InputFields | ToggleGroupField | FormDropdownProps
  ): field is ToggleGroupField => field.type === FieldTypes.ToggleGroup;

  const isDropdownField = (
    field: InputFields | ToggleGroupField | FormDropdownProps
  ): field is FormDropdownProps => field.type === FieldTypes.Dropdown;

  const handleInputField = (
    field: InputFields | ToggleGroupField | FormDropdownProps
  ) => {
    switch (field.type) {
      case FieldTypes.TextInputSingleLine:
        if (isInputField(field)) {
          return (
            <FormInputText
              label={translateFormField(field.label)}
              defaultValue={field.defaultValue ?? ''}
              pattern={field.pattern ?? null}
              password={field.password ?? false}
              required={field.required ?? false}
              guideMe={field.guideMe ?? false}
              fieldDescription={translatePlaceholder(field.inputDescription)}
              payloadAtrributeName={field.payloadAtrributeName ?? ''}
              attributeName={field.attributeName ?? ''}
              inputValueStatus={field.inputValueStatus ?? (() => {})}
              fieldDisabled={field.fieldDisabled ?? false}
              kind={field.kind}
              isTooltip={field.isTooltip}
              tooltipText={field.tooltipText}
              tooltipIconColor={field.tooltipIconColor}
              errorMessage={field.errorMessage}
              placeholder={translatePlaceholder(field.placeholder)}
            />
          );
        }

        break;

      case FieldTypes.CheckBoxes:
        if (isInputField(field) && 'options' in field) {
          const validOptions = Array.isArray(field.options)
            ? field.options
            : [];

          return (
            <div>
              {validOptions.map((option) => (
                <div key={option.id ?? option.label}>
                  <input type='checkbox' id={option.id ?? option.label} />
                  <label htmlFor={option.id ?? option.label}>
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
          );
        }

        break;

      case FieldTypes.ToggleGroup:
        if (isToggleGroupField(field)) {
          return (
            <FormToggleGroup
              label={translateFormField(field.label)}
              options={field.options}
              visibleCount={field.visibleCount}
              selection={field.selection}
              setSelection={field.setSelection}
              onSelectionChange={field.onSelectionChange}
              size={field.size}
              isPill={field.isPill}
              isDisabled={field.isDisabled}
              className={field.className}
              required={field.required}
              payloadAtrributeName={field.payloadAtrributeName}
              attributeName={field.attributeName}
              fieldDisabled={field.fieldDisabled}
              inputValueStatus={field.inputValueStatus}
              type={FieldTypes.ToggleGroup}
            />
          );
        }

        break;

      case FieldTypes.Dropdown:
        if (isDropdownField(field)) {
          return (
            <FormDropdown
              type={FieldTypes.Dropdown}
              attributeName={field.attributeName}
              payloadAtrributeName={field.payloadAtrributeName}
              options={field.options}
              required={field.required}
              fieldDisabled={field.fieldDisabled}
              inputValueStatus={field.inputValueStatus}
              hasMoreOptions={field.hasMoreOptions}
              isLoading={field.isLoading}
              placeholder={translatePlaceholder(field.placeholder)}
              config={field.config}
              fetchData={field.fetchData}
              value={field.value}
              errorMessage={field.errorMessage}
            />
          );
        }

        break;

      default:
        return null;
    }

    return null;
  };

  return (
    <Box className={className}>
      {fields.map((field) => (
        <Box
          key={
            ('key' in field && typeof field.key === 'string' && field.key) ||
            field.attributeName ||
            'field'
          }>
          {handleInputField(field)}
        </Box>
      ))}
    </Box>
  );
}

export default FormBuilder;
